import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ArrowCounterClockwise,
  ArrowClockwise,
  ArrowsLeftRight,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  Copy,
  DownloadSimple,
  FilePdf,
  FloppyDisk,
  ImageSquare,
  Info,
  LinkSimple,
  Tag,
  Trash,
  UploadSimple,
} from "@phosphor-icons/react";
import { SeamPreview } from "@/components/SeamPreview";
import { Countertop3D } from "@/components/Countertop3D";
import { rotatePoints } from "@/components/SlabLayoutBoard";
import { portalApi, portalAsset } from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";
import { shrinkImage } from "@/lib/shrinkImage";
import { svgToPng, downloadDataUrl } from "@/lib/svgExport";
import { uid } from "@/lib/quoteAreas";

const snap = (v) => Math.round(v * 4) / 4;
const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

// scene presets behind the Shape / Room / All scope buttons on the portal 3D preview
const SCENE_BY_SCOPE = {
  shape: { cabinets: false, appliances: false, walls: false, uppers: false },
  room: { cabinets: true, appliances: true, walls: true, uppers: false },
  all: { cabinets: true, appliances: true, walls: true, uppers: true },
};

const photoBox = (s) => {
  const zoom = Math.max(0.1, num(s.photo_zoom, 1) || 1);
  return { x: num(s.photo_x), y: num(s.photo_y), width: num(s.length_in, 1) * zoom, height: num(s.width_in, 1) * zoom };
};

export const quotePieces = (quote) =>
  (quote?.areas || []).flatMap((a) =>
    (a.pieces || []).filter((p) => (p.points || []).length > 2).map((p) => ({ ...p, area_name: a.name || "Area" })),
  );

// slabs the fabricator attached to the quote (only those with a photo are useful here)
export const layoutSlabsFor = (quote) =>
  (quote?.layout?.slabs || [])
    .map((s, i) =>
      s.photo
        ? {
            id: s.id || `L${i}`,
            source: "layout",
            index: i,
            label: s.label || `Slab ${i + 1}`,
            length_in: num(s.length_in, 120),
            width_in: num(s.width_in, 60),
            photo_zoom: num(s.photo_zoom, 1) || 1,
            photo_x: num(s.photo_x),
            photo_y: num(s.photo_y),
            mirror: !!s.mirror,
            photo: s.photo,
          }
        : null,
    )
    .filter(Boolean);

// starting point: the saved customer proposal, else the fabricator's own nesting
export const initialWork = (quote, proposal) => {
  if (proposal?.slabs?.length) {
    return { slabs: proposal.slabs, placements: proposal.placements || {}, note: proposal.note || "" };
  }
  const slabs = layoutSlabsFor(quote);
  const byIndex = Object.fromEntries(slabs.map((s, n) => [s.index, n]));
  const placements = {};
  Object.entries(quote?.layout?.placements || {}).forEach(([pid, pl]) => {
    if (byIndex[pl.slab] !== undefined) placements[pid] = { ...pl, slab: byIndex[pl.slab] };
  });
  return { slabs, placements, note: "" };
};

const SlabBoard = ({ slab, index, pieces, placements, selected, onSelect, onMove, onRotate, dragRef }) => {
  const L = num(slab.length_in, 120);
  const W = num(slab.width_in, 60);
  const on = pieces.filter((p) => placements[p.id]?.slab === index);
  const svgRef = useRef(null);

  const toIn = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    const k = r.width / L;
    return [(e.clientX - r.left) / k, (e.clientY - r.top) / k];
  };
  const startDrag = (e, p) => {
    e.stopPropagation();
    const [x, y] = toIn(e);
    const pl = placements[p.id];
    dragRef.current = { pieceId: p.id, slab: index, gx: x - (pl.x || 0), gy: y - (pl.y || 0) };
    onSelect(p.id);
  };
  const startRotate = (e, p, geo) => {
    e.stopPropagation();
    const pl = placements[p.id];
    const [x, y] = toIn(e);
    const cx = (pl.x || 0) + geo.w / 2;
    const cy = (pl.y || 0) + geo.h / 2;
    dragRef.current = {
      rotating: true, pieceId: p.id, slab: index, cx, cy,
      start: (Math.atan2(y - cy, x - cx) * 180) / Math.PI, rot0: num(pl.rot),
    };
    onSelect(p.id);
  };
  const move = (e) => {
    const d = dragRef.current;
    if (!d || d.slab !== index) return;
    const [x, y] = toIn(e);
    if (d.rotating) {
      const cur = (Math.atan2(y - d.cy, x - d.cx) * 180) / Math.PI;
      onRotate(d.pieceId, ((Math.round(d.rot0 + cur - d.start) % 360) + 360) % 360);
      return;
    }
    onMove(d.pieceId, index, Math.max(0, snap(x - d.gx)), Math.max(0, snap(y - d.gy)));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={`vein-slab-${index}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-slate-800" data-testid={`vein-slab-label-${index}`}>{slab.label}</span>
        {slab.mirror && (
          <span className="rounded-full border border-cyan-300 bg-cyan-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-cyan-700" data-testid={`vein-slab-mirror-${index}`}>
            Book-matched · mirrored
          </span>
        )}
        <span className="ml-auto font-mono text-[11px] text-slate-500">{L}" × {W}" · {on.length} piece{on.length === 1 ? "" : "s"}</span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${L} ${W}`}
        className="w-full select-none rounded-xl border border-slate-200 bg-slate-100"
        style={{ touchAction: "none" }}
        onPointerMove={move}
        onPointerDown={() => onSelect(null)}
        data-testid={`vein-slab-svg-${index}`}
      >
        <defs>
          <clipPath id={`vein-board-clip-${index}`}><rect width={L} height={W} /></clipPath>
        </defs>
        <g clipPath={`url(#vein-board-clip-${index})`}>
          <g transform={slab.mirror ? `translate(${L},0) scale(-1,1)` : undefined}>
            <image href={portalAsset(slab.photo)} preserveAspectRatio="xMidYMid slice" {...photoBox(slab)} />
          </g>
        </g>
        {on.map((p) => {
          const pl = placements[p.id];
          const geo = rotatePoints(p.points, pl.rot || 0);
          const sel = selected === p.id;
          const pts = geo.pts.map(([x, y]) => `${x},${y}`).join(" ");
          const fs = Math.max(2.2, L / 40);
          return (
            <g key={p.id} transform={`translate(${pl.x || 0} ${pl.y || 0})`} data-testid={`vein-piece-${p.id}`}>
              <polygon
                points={pts}
                fill={sel ? "rgba(6,182,212,0.28)" : "rgba(255,255,255,0.28)"}
                stroke={sel ? "#0891b2" : "#ffffff"}
                strokeWidth={sel ? 0.7 : 0.45}
                strokeDasharray={sel ? undefined : "1.5 1"}
                className="cursor-grab"
                onPointerDown={(e) => startDrag(e, p)}
              />
              <text x={geo.w / 2} y={geo.h / 2} textAnchor="middle" fontSize={fs} fontWeight="700" fill="#fff" stroke="rgba(0,0,0,0.55)" strokeWidth={fs * 0.12} paintOrder="stroke" className="pointer-events-none">
                {p.label}
              </text>
              {sel && (
                <g className="cursor-crosshair" onPointerDown={(e) => startRotate(e, p, geo)} data-testid={`vein-rotate-${p.id}`}>
                  <line x1={geo.w} y1={0} x2={geo.w + fs * 1.4} y2={-fs * 1.4} stroke="#0891b2" strokeWidth={0.35} />
                  <circle cx={geo.w + fs * 1.4} cy={-fs * 1.4} r={fs * 0.7} fill="#0891b2" stroke="#fff" strokeWidth={0.35} />
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export const PortalVeinStudio = ({ quote, proposal, onSaved }) => {
  const pieces = useMemo(() => quotePieces(quote), [quote]);
  const [work, setWork] = useState(() => initialWork(quote, proposal));
  const [selected, setSelected] = useState(null);
  const [labels, setLabels] = useState(true);
  const [viewMode, setViewMode] = useState("2d");
  const [scope, setScope] = useState("room");
  const [busy, setBusy] = useState("");
  const [share, setShare] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showTip, setShowTip] = useState(() => {
    try {
      return !localStorage.getItem("bgw_vein_tip_v1");
    } catch {
      return false;
    }
  });
  const dismissTip = () => {
    setShowTip(false);
    try {
      localStorage.setItem("bgw_vein_tip_v1", "1");
    } catch {
      /* ignore */
    }
  };
  const dragRef = useRef(null);
  const fileRef = useRef(null);
  const previewRef = useRef(null);
  const boardsRef = useRef(null);

  useEffect(() => {
    setWork(initialWork(quote, proposal));
    setDirty(false);
  }, [quote, proposal]);

  const { slabs, placements } = work;
  const update = (fn) => {
    setWork((w) => fn(w));
    setDirty(true);
  };
  const setPlacement = (pid, patch) =>
    update((w) => ({ ...w, placements: { ...w.placements, [pid]: { ...(w.placements[pid] || {}), ...patch } } }));
  const movePiece = (pid, slab, x, y) => setPlacement(pid, { slab, x, y });
  const rotateTo = (pid, rot) => setPlacement(pid, { rot: Math.round(rot * 10) / 10 });
  const rotateBy = (d) => selected && placements[selected] && rotateTo(selected, (((num(placements[selected].rot) + d) % 360) + 360) % 360);
  const nudge = (dx, dy) => {
    const pl = placements[selected];
    if (pl) movePiece(selected, pl.slab, Math.max(0, (pl.x || 0) + dx), Math.max(0, (pl.y || 0) + dy));
  };
  const place = (pid, slab) => setPlacement(pid, { slab, x: 2, y: 2, rot: 0 });
  const unplace = (pid) =>
    update((w) => {
      const next = { ...w.placements };
      delete next[pid];
      return { ...w, placements: next };
    });

  const selPl = selected ? placements[selected] : null;
  const selSlab = selPl ? slabs[selPl.slab] : null;

  // Book-match: cut the piece from a mirrored twin of its slab so the veins meet
  // at the seam like an open book.
  const toggleBookmatch = () => {
    if (!selPl || !selSlab) return;
    update((w) => {
      let next = [...w.slabs];
      let target;
      if (selSlab.twin_of) {
        target = next.findIndex((s) => s.id === selSlab.twin_of);
        if (target < 0) return w;
      } else {
        target = next.findIndex((s) => s.twin_of === selSlab.id);
        if (target < 0) {
          next.push({ ...selSlab, id: uid(), mirror: !selSlab.mirror, twin_of: selSlab.id, label: `${selSlab.label} · twin` });
          target = next.length - 1;
        }
      }
      const placementsNext = { ...w.placements, [selected]: { ...w.placements[selected], slab: target } };
      // drop an emptied twin and re-index anything after it
      const emptyTwin = next.findIndex((s, n) => s.twin_of && !Object.values(placementsNext).some((pl) => pl.slab === n));
      if (emptyTwin >= 0) {
        next = next.filter((_, n) => n !== emptyTwin);
        Object.keys(placementsNext).forEach((pid) => {
          if (placementsNext[pid].slab > emptyTwin) placementsNext[pid] = { ...placementsNext[pid], slab: placementsNext[pid].slab - 1 };
        });
      }
      return { ...w, slabs: next, placements: placementsNext };
    });
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", await shrinkImage(file));
      const { data } = await portalApi.post(`/quotes/${quote.id}/vein-proposal/photo`, fd);
      const ref = slabs[0];
      update((w) => ({
        ...w,
        slabs: [
          ...w.slabs,
          {
            id: uid(), source: "upload", photo_name: data.name, photo: data.photo, label: "My slab photo",
            length_in: ref ? ref.length_in : 126, width_in: ref ? ref.width_in : 63,
            photo_zoom: 1, photo_x: 0, photo_y: 0, mirror: false,
          },
        ],
      }));
      toast.success("Photo added — drag your pieces onto it");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy("");
    }
  };

  const setSlabSize = (n, key, v) =>
    update((w) => ({ ...w, slabs: w.slabs.map((s, i) => (i === n ? { ...s, [key]: Math.max(12, num(v, 12)) } : s)) }));

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await portalApi.put(`/quotes/${quote.id}/vein-proposal`, {
        slabs: slabs.map(({ photo, ...s }) => s),
        placements,
        note: work.note || "",
      });
      onSaved?.(data.proposal);
      setDirty(false);
      toast.success("Saved — your fabricator can see this layout");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setBusy("");
    }
  };

  const renderAll = async () => {
    const preview = previewRef.current?.querySelector("svg");
    const out = {};
    if (preview) out.preview_png = await svgToPng(preview, { width: 1800, background: "#f8fafc" });
    if (boardsRef.current) out.layout_png = await svgToPng(boardsRef.current, { width: 1800, background: "#ffffff" });
    return out;
  };

  const downloadPng = async () => {
    setBusy("png");
    try {
      const { preview_png } = await renderAll();
      if (!preview_png) throw new Error("nothing");
      downloadDataUrl(preview_png, `vein-match-${quote.code || "quote"}.png`);
    } catch {
      toast.error("Could not render the image");
    } finally {
      setBusy("");
    }
  };

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      const images = await renderAll();
      const { data } = await portalApi.post(`/quotes/${quote.id}/vein-proposal/export`, { ...images, note: work.note || "" });
      const origin = process.env.REACT_APP_BACKEND_URL;
      const links = { ...data, pdf_url: `${origin}${data.pdf_path}`, png_url: `${origin}${data.png_path}` };
      setShare(links);
      window.open(links.pdf_url, "_blank", "noopener");
      toast.success("PDF ready — share link copied below");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Export failed");
    } finally {
      setBusy("");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the link instead");
    }
  };

  const unplaced = pieces.filter((p) => !placements[p.id]);
  const photoOf = (s) => (s.photo ? portalAsset(s.photo) : "");
  // 3D/CAD should reflect the customer's current draft arrangement, not the shop's saved layout
  const scene3dQuote = useMemo(
    () => ({ ...quote, layout: { ...(quote.layout || {}), slabs, placements } }),
    [quote, slabs, placements],
  );
  const effScene =
    viewMode === "cad"
      ? { mode: "light", cabinets: false, appliances: false, walls: false, uppers: false, sinks: false }
      : { mode: "light", ...SCENE_BY_SCOPE[scope] };
  const modeCls = (a) =>
    `inline-flex h-7 items-center rounded-full px-3 text-[10px] font-bold uppercase tracking-widest transition-colors ${
      a ? "bg-cyan-500 text-white" : "bg-white text-slate-500 ring-1 ring-slate-300 hover:text-slate-800"
    }`;
  const installRot = quote?.layout?.install_rot || {};
  const seamPieces = pieces
    .filter((p) => !p.panel_kind)
    .map((p) => {
      const ir = installRot[p.id] || 0;
      return ir ? { ...p, rot: ((((Number(p.rot) || 0) + ir) % 360) + 360) % 360 } : p;
    });
  const boardsH = slabs.reduce((h, s) => h + (num(s.width_in, 60) / num(s.length_in, 120)) * 1000 + 40, 0);

  return (
    <div data-testid="portal-vein-studio" className="space-y-5">
      {showTip && (
        <div
          className="flex items-start gap-3 rounded-2xl border border-[#c39e6d]/30 bg-[#faf6ef] px-4 py-3"
          data-testid="vein-first-tip"
        >
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#c39e6d]/15 text-[#a9855a]">
            <Info size={16} weight="bold" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900">
              See your slab, your way
            </div>
            <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">
              Add a photo of your slab, then drag each countertop piece onto the
              veining you love. Nothing here is final — your fabricator confirms
              everything before it's cut.
            </p>
          </div>
          <button
            type="button"
            data-testid="vein-first-tip-dismiss"
            onClick={dismissTip}
            className="shrink-0 rounded-full border border-[#c39e6d]/40 bg-white px-3 py-1.5 text-xs font-semibold text-[#a9855a] transition-colors duration-150 hover:bg-[#c39e6d]/10"
          >
            Got it
          </button>
        </div>
      )}
      {/* toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" data-testid="vein-upload" disabled={!!busy} onClick={() => fileRef.current?.click()} className="flex h-10 items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:border-[#c39e6d] hover:text-slate-900 disabled:opacity-50">
          <UploadSimple size={15} weight="bold" /> {busy === "upload" ? "Uploading…" : "Add my slab photo"}
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="vein-upload-input" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        <button type="button" data-testid="vein-labels" onClick={() => setLabels((v) => !v)} className={`flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-semibold transition-colors duration-150 ${labels ? "border-slate-800 bg-slate-800 text-white" : "border-slate-300 bg-white text-slate-600"}`}>
          <Tag size={15} weight="bold" /> Labels
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button type="button" data-testid="vein-download-png" disabled={!!busy || !Object.keys(placements).length} onClick={downloadPng} className="flex h-10 items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-500 disabled:opacity-50">
            <ImageSquare size={15} weight="bold" /> {busy === "png" ? "Rendering…" : "Image"}
          </button>
          <button type="button" data-testid="vein-export-pdf" disabled={!!busy || !Object.keys(placements).length} onClick={exportPdf} className="flex h-10 items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-500 disabled:opacity-50">
            <FilePdf size={15} weight="bold" /> {busy === "pdf" ? "Building…" : "PDF & share link"}
          </button>
          <button type="button" data-testid="vein-save" disabled={!!busy || !Object.keys(placements).length} onClick={save} className={`flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold text-white transition-colors duration-150 disabled:opacity-50 ${dirty ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-800 hover:bg-slate-900"}`}>
            <FloppyDisk size={15} weight="bold" /> {busy === "save" ? "Saving…" : "Save & send to fabricator"}
          </button>
        </div>
      </div>

      {share && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm" data-testid="vein-share-panel">
          <LinkSimple size={16} weight="bold" className="text-emerald-700" />
          <span className="font-semibold text-emerald-900">Shareable links</span>
          <a href={share.pdf_url} target="_blank" rel="noreferrer" className="underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900" data-testid="vein-share-pdf">PDF</a>
          <button type="button" onClick={() => copy(share.pdf_url)} className="flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800" data-testid="vein-copy-pdf"><Copy size={12} /> Copy</button>
          <a href={share.png_url} target="_blank" rel="noreferrer" className="underline decoration-emerald-400 underline-offset-2 hover:text-emerald-900" data-testid="vein-share-png">Image</a>
          <button type="button" onClick={() => copy(share.png_url)} className="flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-800" data-testid="vein-copy-png"><Copy size={12} /> Copy</button>
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[5fr_4fr]" onPointerUp={() => { dragRef.current = null; }} onPointerLeave={() => { dragRef.current = null; }}>
        {/* slab boards */}
        <div className="min-w-0 space-y-4">
          <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-slate-500">
            <span>Your slab{slabs.length === 1 ? "" : "s"} — drag pieces to where they should be cut</span>
            <span className="font-mono normal-case tracking-normal">{pieces.length - unplaced.length}/{pieces.length} placed</span>
          </div>
          {slabs.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500" data-testid="vein-no-slabs">
              No slab photo on this quote yet — add a photo of your slab to start.
            </div>
          )}
          {slabs.map((s, i) => (
            <div key={s.id} className="space-y-2">
              <SlabBoard slab={s} index={i} pieces={pieces} placements={placements} selected={selected} onSelect={setSelected} onMove={movePiece} onRotate={rotateTo} dragRef={dragRef} />
              {s.source === "upload" && (
                <div className="flex flex-wrap items-center gap-2 px-1 text-xs text-slate-600" data-testid={`vein-slab-size-${i}`}>
                  <span>Slab size</span>
                  <input type="number" value={s.length_in} onChange={(e) => setSlabSize(i, "length_in", e.target.value)} className="h-8 w-20 rounded-lg border border-slate-300 px-2 font-mono" data-testid={`vein-slab-length-${i}`} />
                  <span>×</span>
                  <input type="number" value={s.width_in} onChange={(e) => setSlabSize(i, "width_in", e.target.value)} className="h-8 w-20 rounded-lg border border-slate-300 px-2 font-mono" data-testid={`vein-slab-width-${i}`} />
                  <span>inches</span>
                </div>
              )}
            </div>
          ))}

          {selPl && (
            <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-cyan-200 bg-cyan-50 p-3" data-testid="vein-piece-tools">
              <span className="text-sm font-semibold text-cyan-900">{pieces.find((p) => p.id === selected)?.label}</span>
              <div className="flex items-center gap-1 rounded-full border border-cyan-200 bg-white p-1">
                {[["left", CaretLeft, [-1, 0]], ["right", CaretRight, [1, 0]], ["up", CaretUp, [0, -1]], ["down", CaretDown, [0, 1]]].map(([k, Icon, [dx, dy]]) => (
                  <button key={k} type="button" data-testid={`vein-nudge-${k}`} onClick={() => nudge(dx, dy)} className="flex h-8 w-8 items-center justify-center rounded-full text-cyan-800 hover:bg-cyan-100" aria-label={`Nudge ${k}`}><Icon size={14} weight="bold" /></button>
                ))}
              </div>
              <div className="flex items-center gap-1 rounded-full border border-cyan-200 bg-white p-1">
                <button type="button" data-testid="vein-rotate-ccw" onClick={() => rotateBy(-90)} className="flex h-8 items-center gap-1 rounded-full px-3 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"><ArrowCounterClockwise size={14} weight="bold" /> 90°</button>
                <button type="button" data-testid="vein-rotate-cw" onClick={() => rotateBy(90)} className="flex h-8 items-center gap-1 rounded-full px-3 text-xs font-semibold text-cyan-800 hover:bg-cyan-100"><ArrowClockwise size={14} weight="bold" /> 90°</button>
                <button type="button" data-testid="vein-rotate-fine" onClick={() => rotateBy(5)} className="flex h-8 items-center rounded-full px-3 text-xs font-semibold text-cyan-800 hover:bg-cyan-100">+5°</button>
                <span className="px-2 font-mono text-xs text-cyan-900" data-testid="vein-rotation-value">{num(selPl.rot)}°</span>
              </div>
              <button type="button" data-testid="vein-bookmatch" onClick={toggleBookmatch} className={`flex h-9 items-center gap-2 rounded-full border px-4 text-xs font-semibold transition-colors duration-150 ${selSlab?.twin_of ? "border-cyan-700 bg-cyan-700 text-white" : "border-cyan-300 bg-white text-cyan-900 hover:bg-cyan-100"}`} title="Cut this piece from a mirrored twin slab so the veins meet like an open book">
                <ArrowsLeftRight size={14} weight="bold" /> {selSlab?.twin_of ? "Book-matched" : "Book-match this piece"}
              </button>
              {slabs.length > 1 && (
                <select data-testid="vein-move-slab" value={selPl.slab} onChange={(e) => movePiece(selected, Number(e.target.value), 2, 2)} className="h-9 rounded-full border border-cyan-300 bg-white px-3 text-xs font-semibold text-cyan-900">
                  {slabs.map((s, i) => <option key={s.id} value={i}>On: {s.label}</option>)}
                </select>
              )}
              <button type="button" data-testid="vein-unplace" onClick={() => { unplace(selected); setSelected(null); }} className="ml-auto flex h-9 items-center gap-1 rounded-full px-3 text-xs font-semibold text-red-600 hover:bg-red-50"><Trash size={14} weight="bold" /> Take off slab</button>
            </div>
          )}

          {unplaced.length > 0 && slabs.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid="vein-unplaced">
              <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Not on a slab yet</div>
              <ul className="mt-2 space-y-1.5">
                {unplaced.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-800">{p.label}</span>
                    <span className="text-xs text-slate-500">{p.area_name}</span>
                    <span className="ml-auto flex flex-wrap justify-end gap-1">
                      {slabs.map((s, i) => (
                        <button key={s.id} type="button" data-testid={`vein-place-${p.id}-${i}`} onClick={() => { place(p.id, i); setSelected(p.id); }} className="max-w-full truncate rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:border-[#c39e6d]">
                          Place on {slabs.length > 1 ? s.label : "slab"}
                        </button>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* seam preview */}
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">As installed — how the vein reads through the seams</div>
            <div data-testid="portal-vein-view-modes" className="flex flex-wrap items-center gap-1">
              {[["2d", "2D"], ["3d", "3D"], ["cad", "CAD"]].map(([m, l]) => (
                <button key={m} type="button" data-testid={`portal-vein-mode-${m}`} onClick={() => setViewMode(m)} className={modeCls(viewMode === m)}>{l}</button>
              ))}
              {viewMode === "3d" && (
                <>
                  <span className="mx-1 h-4 w-px bg-slate-300" />
                  {[["shape", "Shape"], ["room", "Room"], ["all", "All"]].map(([s, l]) => (
                    <button key={s} type="button" data-testid={`portal-vein-scope-${s}`} onClick={() => setScope(s)} className={modeCls(scope === s)}>{l}</button>
                  ))}
                </>
              )}
            </div>
          </div>
          {viewMode === "2d" ? (
          <div ref={previewRef} className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="vein-seam-preview">
            <SeamPreview pieces={seamPieces} slabs={slabs} placements={placements} labels={labels} selected={selected} onSelect={setSelected} light tight seams photoOf={photoOf} />
          </div>
          ) : (
          <div data-testid={viewMode === "cad" ? "portal-vein-cad" : "portal-vein-3d"} className="relative h-[52vh] min-h-[360px] overflow-hidden rounded-2xl border border-slate-200 bg-slate-900">
            <Countertop3D quote={scene3dQuote} photoOf={photoOf} scene={effScene} height="100%" toolbar={false} cad={viewMode === "cad"} />
          </div>
          )}
          <p className="text-xs leading-relaxed text-slate-500">
            Grey pieces aren't on a slab yet. Select a piece and tap <strong>Book-match</strong> to cut it from a mirrored twin slab, so the veins meet at the seam like an open book.
          </p>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">Note for the fabricator</span>
            <textarea data-testid="vein-note" value={work.note || ""} onChange={(e) => update((w) => ({ ...w, note: e.target.value }))} rows={2} placeholder="e.g. Please keep the dark vein running toward the window" className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#c39e6d]" />
          </label>
        </div>
      </div>

      {/* off-screen composites used for the PNG/PDF export */}
      <div className="pointer-events-none fixed -left-[20000px] top-0 w-[1000px]" aria-hidden="true">
        <svg ref={boardsRef} viewBox={`0 0 1000 ${Math.max(boardsH, 100)}`} width="1000" height={Math.max(boardsH, 100)} xmlns="http://www.w3.org/2000/svg">
          {slabs.reduce((acc, s, i) => {
            const L = num(s.length_in, 120);
            const W = num(s.width_in, 60);
            const h = (W / L) * 1000;
            const y = acc.y;
            const on = pieces.filter((p) => placements[p.id]?.slab === i);
            acc.nodes.push(
              <g key={s.id} transform={`translate(0 ${y})`}>
                <text x="4" y="22" fontSize="20" fontWeight="700" fill="#0f172a">{s.label}{s.mirror ? " · mirrored (book-match)" : ""} — {L}" × {W}"</text>
                <svg x="0" y="34" width="1000" height={h} viewBox={`0 0 ${L} ${W}`}>
                  <rect width={L} height={W} fill="#e2e8f0" />
                  <g transform={s.mirror ? `translate(${L},0) scale(-1,1)` : undefined}>
                    <image href={portalAsset(s.photo)} preserveAspectRatio="xMidYMid slice" {...photoBox(s)} />
                  </g>
                  {on.map((p) => {
                    const pl = placements[p.id];
                    const geo = rotatePoints(p.points, pl.rot || 0);
                    const fs = Math.max(2.2, L / 40);
                    return (
                      <g key={p.id} transform={`translate(${pl.x || 0} ${pl.y || 0})`}>
                        <polygon points={geo.pts.map(([x, yy]) => `${x},${yy}`).join(" ")} fill="rgba(255,255,255,0.25)" stroke="#fff" strokeWidth={0.6} />
                        <text x={geo.w / 2} y={geo.h / 2} textAnchor="middle" fontSize={fs} fontWeight="700" fill="#fff" stroke="rgba(0,0,0,0.55)" strokeWidth={fs * 0.12} paintOrder="stroke">{p.label}</text>
                      </g>
                    );
                  })}
                </svg>
              </g>,
            );
            acc.y += h + 40;
            return acc;
          }, { y: 0, nodes: [] }).nodes}
        </svg>
      </div>
    </div>
  );
};

export default PortalVeinStudio;
